import {CircleBadge, Link} from '@primer/react'
import {Box} from '@primer/styled-react'
import {Tooltip} from '@primer/react/deprecated'
import {Octicon} from '@primer/styled-react/deprecated'
import {DownloadIcon, VerifiedIcon} from '@primer/octicons-react'
import type {ModelListing} from '@github-ui/marketplace-common'
import type {
  MarketplaceListingResult,
  MarketplaceResults,
  RepositoryActionResult,
  RepositoryStackResult,
} from '../../types/blackbird-types'
import {assertNever} from '../../utilities/assert-never'
import {Count} from '@github-ui/count'
import type {PropsWithChildren} from 'react'
import Result from '../search-result'
import TopicLabel from '../search-result/TopicLabel'
import {marketplaceCategoryPath, modelsCatalogPath, ownerPath} from '@github-ui/paths'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {PublisherAvatar as ModelsPublisherAvatar} from '@github-ui/github-models/PublisherAvatar'

import styles from './MarketplaceListings.module.css'

export default function MarketplaceListings({results}: {results: MarketplaceResults}) {
  return (
    <Result.List>
      {results.results.map(item => {
        switch (item.type) {
          case 'model':
            return <GitHubModelResultItem key={`model-result-${item.id}`} item={item} />
          default:
            return <MarketplaceResultItem key={`marketplace-result-${item.id}`} item={item} />
        }
      })}
    </Result.List>
  )
}

export function MarketplaceResultItem({
  item,
}: {
  item: MarketplaceListingResult | RepositoryActionResult | RepositoryStackResult
}) {
  return (
    <Result>
      <Result.Header>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Result.Avatar>
            <MarketplaceResultAvatar item={item} />
          </Result.Avatar>

          <Result.Title>
            <Link href={item.resource_path}>{item.name}</Link>
          </Result.Title>

          {item.type === 'marketplace_listing' && item.state === 'verified' && (
            <div>
              <Tooltip aria-label="Verified by GitHub">
                <Octicon
                  icon={VerifiedIcon}
                  size={16}
                  sx={{
                    marginLeft: 1,
                    color: 'success.fg',
                  }}
                />
              </Tooltip>
            </div>
          )}
        </Box>
      </Result.Header>

      <Result.Content>
        <span>{item.type === 'marketplace_listing' ? item.short_description : item.description}</span>
      </Result.Content>

      {(item.primary_category || item.secondary_category) && (
        <Result.TokenList>
          {item.primary_category && (
            <TopicLabel href={marketplaceCategoryPath({categoryName: item.primary_category})}>
              {item.primary_category}
            </TopicLabel>
          )}
          {item.secondary_category && (
            <TopicLabel href={marketplaceCategoryPath({categoryName: item.secondary_category})}>
              {item.secondary_category}
            </TopicLabel>
          )}
        </Result.TokenList>
      )}

      <Result.Footer>
        <Result.FooterItem>
          By&nbsp;
          <Link inline href={ownerPath({owner: item.owner_login})}>
            {item.owner_login}
          </Link>
        </Result.FooterItem>
        <Result.FooterItem>
          {item.type === 'marketplace_listing'
            ? 'Application'
            : item.type === 'repository_action'
              ? 'GitHub Action'
              : 'GitHub Stack'}
        </Result.FooterItem>
        {item.type === 'marketplace_listing' && (
          <Result.FooterItem>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <Octicon
                icon={DownloadIcon}
                size={16}
                sx={{
                  mr: 1,
                }}
              />
              <Count ariaLabel={`${item.installation_count} downloads`} value={item.installation_count} />
            </Box>
          </Result.FooterItem>
        )}
      </Result.Footer>
    </Result>
  )
}

const modelTokenLabel = (n: number) => `${Math.round(n / 1000)}k`

export function GitHubModelResultItem({item}: {item: ModelListing}) {
  return (
    <Result>
      <Result.Header>
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Result.Avatar>
            <Link href={item.model_url}>
              <ModelsPublisherAvatar
                logoUrl={item.logo_url}
                darkModeIcon={item.dark_mode_icon}
                publisher={item.publisher}
              />
            </Link>
          </Result.Avatar>

          <Result.Title>
            <Link href={item.model_url}>{item.friendly_name ?? item.name}</Link>
          </Result.Title>
        </Box>
      </Result.Header>

      <Result.Content>
        <span>{item.summary}</span>
      </Result.Content>

      <Result.TokenList>
        <TopicLabel href={modelsCatalogPath({task: item.task})}>{item.task}</TopicLabel>
        {item.tags.map(tag => (
          <TopicLabel href={modelsCatalogPath({category: tag})} key={`${item.id}-${tag}`}>
            {tag}
          </TopicLabel>
        ))}
      </Result.TokenList>

      <Result.Footer>
        <Result.FooterItem>By {item.publisherDisplayName ?? item.publisher}</Result.FooterItem>
        {item.max_input_tokens !== undefined && (
          <Result.FooterItem>{modelTokenLabel(item.max_input_tokens)} input</Result.FooterItem>
        )}
        {item.max_output_tokens !== null && (
          <Result.FooterItem>{modelTokenLabel(item.max_output_tokens)} output</Result.FooterItem>
        )}
      </Result.Footer>
    </Result>
  )
}

function MarketplaceResultAvatar({
  item,
}: {
  item: MarketplaceListingResult | RepositoryActionResult | RepositoryStackResult
}) {
  const avatarSize = 12

  switch (item.type) {
    case 'marketplace_listing':
      return (
        <MarketplaceCircleBadge color={addHashToColor(item.marketplace_listing.listing.bgcolor)}>
          <GitHubAvatar size={avatarSize} src={item.development_logo || item.listing_logo_url} />
        </MarketplaceCircleBadge>
      )
    case 'repository_action':
      return item.icon_svg ? (
        <MarketplaceCircleBadge color={addHashToColor(item.repository_action.repository_action.color)}>
          <MarketplaceSvgAvatar size={avatarSize} svg={item.icon_svg} />
        </MarketplaceCircleBadge>
      ) : null
    case 'repository_stack':
      return item.icon_svg ? (
        <MarketplaceCircleBadge color={addHashToColor(item.repository_stack.repository_stack.color)}>
          <MarketplaceSvgAvatar size={avatarSize} svg={item.icon_svg} />
        </MarketplaceCircleBadge>
      ) : null
    default:
      assertNever(item)
  }
}

function addHashToColor(color: string): string {
  return `#${color}`
}

function MarketplaceCircleBadge({color, children}: PropsWithChildren<{color: string}>) {
  return (
    <CircleBadge size={20} className={styles.CircleBadge} style={{backgroundColor: color}}>
      {children}
    </CircleBadge>
  )
}

function MarketplaceSvgAvatar({size, svg}: {size: number; svg: SafeHTMLString}) {
  return (
    <VerifiedHTMLBox
      html={svg}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        // Override the size of the SVG
        '> *': {
          height: size,
          width: size,
        },
      }}
    />
  )
}
